import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { CreateContentCard } from "../../modules/create-content-card/create-content-card";
import { CreateContentCardNew } from "../../modules/create-content-card/create-content-card-new";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import {
  SupabaseMomentsRecentlyEditedService,
  SupabaseMomentsSentService,
  SupabaseMomentsUpcomingService,
  type MomentCardMomentActivity,
} from "../../modules/moment/server/supabase-moment-service";
import { useMultiformat } from "../../modules/multiformat/hooks/use-multi-format";
import { useFlag } from "../../server/optimizely";
import { useSupabase } from "../../server/supabase/hooks";
import { CreateFromScratchCard } from "../moments/cards/create-from-scratch-card";
import { DiscoverCard } from "../moments/cards/discover-card";
import { NewsletterCard } from "../moments/cards/newsletter-card";
import { MomentCarousels } from "../moments/dashboard/moment-carousels";
import { useCustomTemplates } from "./use-custom-templates";

const CreateContentPage: FC = () => {
  const [new_page, , flagLoaded] = useFlag("create_content_page_v2");
  const { t } = useTranslation();

  if (!flagLoaded)
    return <Page title={t("Create Content | ChangeEngine")} sx={{ maxWidth: "xl" }}></Page>;

  if (new_page) return <CreateContentPageNew />;

  return <CreateContentPageOld />;
};

const CreateContentPageNew: FC = () => {
  const { t } = useTranslation();
  const { data: multiformat, error: multiformatError } = useMultiformat();
  useCustomTemplates(multiformat);
  const [recentlyEdited, setRecentlyEdited] = useState<MomentCardMomentActivity[]>();
  const [sent, setSent] = useState<MomentCardMomentActivity[]>();
  const [upcoming, setUpcoming] = useState<MomentCardMomentActivity[]>();

  const limit = 24;

  useSupabase(({ supabase, account_id }) => {
    return new SupabaseMomentsRecentlyEditedService(supabase)
      .getAllMoments(account_id, { limit, order: [{ column: "attime", dir: "desc" }] })
      .then(({ data, error }) => {
        if (!error) {
          setRecentlyEdited(data);
        }
      });
  }, []);

  useSupabase(({ supabase, account_id }) => {
    return new SupabaseMomentsSentService(supabase)
      .getAllMoments(account_id, { limit, order: [{ column: "attime", dir: "desc" }] })
      .then(({ data, error }) => {
        if (!error) {
          setSent(data);
        }
      });
  }, []);

  useSupabase(({ supabase, account_id }) => {
    return new SupabaseMomentsUpcomingService(supabase)
      .getAllMoments(account_id, { limit, order: [{ column: "attime", dir: "asc" }] })
      .then(({ data, error }) => {
        if (!error) {
          setUpcoming(data);
        }
      });
  }, []);

  return (
    <Page title={t("Create Content | ChangeEngine")}>
      {multiformatError ? <MultiformatLoadingError /> : <CreateContentCardNew />}
      <PageContent>
        <MomentCarousels recentlyEdited={recentlyEdited} sent={sent} upcoming={upcoming} />
      </PageContent>
    </Page>
  );
};

const CreateContentPageOld: FC = () => {
  const { t } = useTranslation();

  const { data: multiformat, error } = useMultiformat();

  useCustomTemplates(multiformat);

  return (
    <Page title={t("Create Content | ChangeEngine")} sx={{ maxWidth: "xl" }}>
      {!error && <CreateContentCard />}
      {error && <MultiformatLoadingError />}
      <Box
        gap={4}
        m={5}
        display="grid"
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            gridTemplateColumns: "repeat(3, 1fr)",
          },
        })}
      >
        <CreateFromScratchCard />
        <DiscoverCard />
        <NewsletterCard />
      </Box>
    </Page>
  );
};

const MultiformatLoadingError: FC = () => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <Box display="flex" gap={2} alignItems="center" p={2}>
          <ErrorOutlineOutlined />
          <Typography>
            {t(
              "There was a problem trying to load our AI content generator. Please refresh your browser to try again, or use one of the options below.",
            )}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CreateContentPage;
